import { tlLogger } from '@taleemabad/shared/utils';
import { REGION_NAMES } from '@taleemabad/shared';
import { type AxiosError } from 'axios';
import { isEqual } from 'lodash';
import { analyticsService } from '../api/analytics';
import { userDetails } from '../api/auth';
import {
  fetchExamGeneratorSupportedConfig,
  fetchFlnAssessments,
  fetchObservationContext,
  fetchInitialObservationTemplates,
  fetchObservationTemplatesRemainingPages,
  fetchObservationFeedbacks,
  fetchProfiles,
  fetchRolesAndPermission,
  fetchVisits,
  fetchAllCoachProfiles,
  fetchSchoolsGroups,
} from '../api';
import { fetchBookChaptersV2 } from '../api/lessonplan';
import { fetchNotifications } from '../api/notification';
import { fetchStudentAssessmentDataV1 } from '../api/students';
import { fetchCourseData } from '../api/teachertraining';
import { analyticsDB } from '../models/analytics';
import { BaseModel, BaseModelForPush } from '../models/base-model';
import { examGeneratorDB } from '../models/examgenerator';
import { flnAssessmentDB } from '../models/fln-assessment';
import { lessonPlanDB } from '../models/lessonplan';
import { notificationDb } from '../models/notification';
import { studentDB } from '../models/students';
import { syncedDBModel } from '../models/synced';
import { teacherTrainingDB } from '../models/teachertraining';
import { coachingDB } from '../models/coaching';
import { digitalCoachChatDB } from '../models/digital-coach-chat';
import { pullChatSessions } from '../api/digital-coach-chat';
import { FeatureFlagNames, userDB } from '../models/user';
import { GenericProfile, UserProfile } from '../types/user';
import { EventNames } from '../types/analytics';
import { IResponseData } from '../types/students';
import { SyncChanges, SyncRecord, SyncRecordStatus } from '../types/sync-state';
import { apiService } from './request';
import { hasFeatureFlagsChanged } from './shared';
import { userCache } from './user-cache';
import { syncStateManager } from './sync-state-manager';
import { Region } from '@taleemabad/dsm/components';

/**
 * This class is the generic glue between the
 * models and worker. It helps pull and push the
 * data from the different tables we have. Each time
 * a new table is added, we should add it here. Read
 * tables are only for pulls, and write tbales are for
 * pushes.
 * */
// User profile is fetched on every sync to keep feature flags, roles, and levels fresh.
// The setUserProfile() method (user.ts:235-236) explicitly preserves the locally-selected
// profile by re-applying isSelected=true to the same profile ID, so frequent updates are safe.

const countChangeRecords = (changes: unknown): number => {
  if (!changes || typeof changes !== 'object') return 0;
  let total = 0;
  for (const tableChanges of Object.values(
    changes as Record<string, unknown>,
  )) {
    if (!tableChanges || typeof tableChanges !== 'object') continue;
    for (const bucket of ['created', 'updated', 'deleted'] as const) {
      const records = (tableChanges as Record<string, unknown>)[bucket];
      if (Array.isArray(records)) total += records.length;
    }
  }
  return total;
};

const readResponseStatus = (responseData: unknown): string => {
  if (!responseData || typeof responseData !== 'object') return 'unknown';
  const status = (responseData as Record<string, unknown>).status;
  return typeof status === 'string' || typeof status === 'number'
    ? String(status)
    : 'unknown';
};

const readErrorCode = (responseData: unknown): string => {
  if (!responseData || typeof responseData !== 'object') return '';
  const code = (responseData as Record<string, unknown>).errorCode;
  return typeof code === 'string' || typeof code === 'number'
    ? String(code)
    : '';
};

class SyncManager<T extends SyncRecord> {
  private pushRetry = false;

  constructor(
    private readTables: { name: string; model: BaseModel }[],
    private writeTables: { name: string; model: BaseModelForPush }[],
    private failedTables: { name: string; model: BaseModelForPush }[] = [],
  ) {}

  async hasUnsyncedChanges(): Promise<boolean> {
    const method = 'SyncManager/hasUnsyncedChanges';
    tlLogger.start(method);
    const result = await syncedDBModel.hasUnsyncedChanges();
    tlLogger.end(method, { result });
    return result;
  }

  async getChangesToSync(): Promise<SyncChanges> {
    const method = 'SyncManager/getChangesToSync';
    tlLogger.start(method);
    const syncChanges: SyncChanges = {};
    const promises = this.writeTables.map(async (table) => {
      const tableChanges = await table.model.getAllUnsyncedRecords();
      const keys = Object.keys(tableChanges);
      await Promise.all(
        keys.map(async (key) => {
          const records = tableChanges[key];
          if (records.length) {
            /**
             * In analytics the events are generated before the user is logged
             * in - so we need to set these manually here.
             */
            for (const record of records) {
              if (record && record?.user === undefined) {
                const profile = userCache.getUserFromCache();
                if (profile) {
                  record.user = profile.id;
                  const selectedProfile = profile.profiles.filter(
                    (p) => p.isSelected,
                  )[0];
                  record.profile = selectedProfile?.id;
                }
              }
            }
            // TODO configure for deletions and updations.
            syncChanges[key] = {
              created: records?.filter(
                (record) =>
                  record?.syncStatus === SyncRecordStatus.Created ||
                  record?.syncStatus === SyncRecordStatus.Failed,
              ),
              updated: records?.filter(
                (records) => records?.syncStatus === SyncRecordStatus.Updated,
              ),
              // to delete the update
              deleted: records?.filter(
                (records) => records?.syncStatus === SyncRecordStatus.Deleted,
              ),
              //   .map((record) => record.id!),
            };
          }
        }),
      );
    });

    await Promise.allSettled(promises);
    tlLogger.end(method, { syncChanges });
    return Promise.resolve(syncChanges);
  }

  async markFailedRecords(
    syncChanges: any,
    requestPayload?: any,
    responseData?: any,
  ) {
    const method = 'SyncManager/postSyncChanges';
    tlLogger.start(method);

    // Track analytics event when records fail to sync.
    //
    // We intentionally emit only lightweight metadata (counts + table names +
    // response status) rather than JSON.stringify-ing the full payloads. The
    // failed records themselves remain in Dexie marked Failed and will be
    // retried — duplicating their content into analyticsDB.events caused
    // geometric growth on flaky networks because each failed-push event is
    // itself part of the next push.
    if (syncChanges && Object.keys(syncChanges).length > 0) {
      const failedTableNames = Object.keys(syncChanges).join(', ');
      analyticsService.trackEvent(EventNames.pushSyncPartialFailure, {
        failedTableNames,
        failedRecordCount: countChangeRecords(syncChanges),
        requestRecordCount: countChangeRecords(requestPayload),
        responseStatus: readResponseStatus(responseData),
        errorCode: readErrorCode(responseData),
      });
    }

    const promises = this.failedTables.map((table) => {
      const { model } = table;
      return model.updateRecordsSyncStatus(
        syncChanges,
        SyncRecordStatus.Failed,
      );
    });
    await Promise.allSettled(promises);
    tlLogger.end(method);
    return Promise.resolve(promises);
  }

  async postSyncChanges(syncChanges: SyncChanges, status: SyncRecordStatus) {
    const method = 'SyncManager/postSyncChanges';
    tlLogger.start(method);

    const promises = this.writeTables.map((table) => {
      const { model } = table;
      // extract ids of records to be mark 'synced'
      // so we can ignore them in the next sync
      // and eventually remove them.
      return model.updateRecordsSyncStatus(syncChanges, status);
    });
    await Promise.allSettled(promises);
    tlLogger.end(method);
    return Promise.resolve(promises);
  }

  async getLastSyncedAt() {
    return await syncedDBModel.getUpdateSyncedAt();
  }

  updateAllSyncStatusesToFailed(syncChanges: SyncChanges) {
    for (const modelKey in syncChanges) {
      const modelChanges = syncChanges[modelKey];

      ['created', 'updated', 'deleted'].forEach((changeType) => {
        const records = modelChanges[changeType as keyof typeof modelChanges];
        if (Array.isArray(records)) {
          records.forEach((record) => {
            record.syncStatus = SyncRecordStatus.Failed;
          });
        }
      });
    }

    return syncChanges;
  }
  updateSyncedAt() {
    const method = 'SyncManager/updateSyncedAt';
    tlLogger.start(method);
    const result = syncedDBModel.updateSyncedAt();
    tlLogger.end(method, { result });
    return result;
  }

  updateSyncStartedAt() {
    const method = 'SyncManager/updateSyncStartedAt';
    tlLogger.start(method);
    const result = syncedDBModel.updateSyncStartedAt();
    tlLogger.end(method, { result });
    return result;
  }

  private async handleActiveProfileValidation(
    method: string,
    fetchUserProfile: UserProfile,
  ) {
    const activeProfile = userCache.getActiveProfile();
    if (!activeProfile) return;

    const isActiveProfileStillValid = fetchUserProfile.profiles.some(
      (profile: GenericProfile) => profile.id === activeProfile.id,
    );

    if (!isActiveProfileStillValid) {
      tlLogger.log(method, {
        message:
          'Active profile has been disabled, clearing active profile selection',
        disabledProfileId: activeProfile.id,
      });

      // Clear active profile selection to trigger profile reselection
      const user = await userDB.getUserProfileFromDB();
      if (user?.profiles) {
        user.profiles.forEach((profile) => {
          profile.isSelected = false;
        });
        await userDB.updateRecord(user);
        userCache.setUserToCache(user);
      }
    }
  }

  async pullChanges(method: string) {
    tlLogger.log(method, { message: 'Pulling changes.' });
    syncStateManager.updateProgress(0, 'Loading...');

    const existingUserDetails = await userDB.getUserProfileFromDB();
    const allFeatureFlags = await userDB.getAllFeatureFlags();

    // Throttle user-profile re-fetches: re-fetch only if the cached copy is
    // absent or stale (>30 min). Tab-switch syncs fire every few seconds and
    // unconditionally overwriting Dexie with the backend payload was the root
    // cause of the level-filter bug (backend returns broad levels such as
    // ['PRIMARY','MIDDLE'] which overwrote the locally-correct active profile).
    let fetchUserProfile = existingUserDetails;

    // Always fetch user profile on every sync to keep feature flags fresh
    fetchUserProfile = await userDetails();
    tlLogger.log(method, { message: 'Fetched fresh user profile from API.' });

    if (!fetchUserProfile?.profiles?.length) {
      tlLogger.log(method, {
        message: 'No profiles available for the user!',
        fetchUserProfile: fetchUserProfile,
      });
      if (fetchUserProfile) {
        const userWithEmptyProfiles = { ...fetchUserProfile, profiles: [] };
        await userDB.updateRecord(userWithEmptyProfiles);
        userCache.setUserToCache(userWithEmptyProfiles);
      }
      return;
    }

    // Always validate active profile since we fetch on every sync
    await this.handleActiveProfileValidation(method, fetchUserProfile);

    const existingProfileIds = (
      existingUserDetails?.profiles.map(
        (profile: GenericProfile) => profile.id,
      ) || []
    ).sort();
    const newProfileIds = (
      fetchUserProfile.profiles.map((profile: GenericProfile) => profile.id) ||
      []
    ).sort();
    const hasProfilesChanged =
      JSON.stringify(existingProfileIds) !== JSON.stringify(newProfileIds);

    if (
      (allFeatureFlags &&
        hasFeatureFlagsChanged(
          fetchUserProfile.featureFlags,
          allFeatureFlags,
        )) ||
      hasProfilesChanged
    ) {
      await syncedDBModel.resetLastSyncedAt();
    }

    const lastSyncedAt = await this.getLastSyncedAt();

    syncStateManager.updateProgress(10, 'Loading your profile...');

    const isExamGeneratorEnabled =
      (await userDB.getFeatureFlag(FeatureFlagNames.examGeneratorEnabled)) ||
      false;

    const existingRegionString = localStorage.getItem('selectedRegion');
    let existingRegion: Region | null = null;
    existingRegion = existingRegionString
      ? JSON.parse(existingRegionString)
      : null;
    const notSedBalochistanRegion =
      existingRegion?.name === REGION_NAMES.SED_BALOCHISTAN;

    // ── Telemetry helpers (no behavior change; measure-only) ──────────────────
    // Total wall time and per-tier/per-API durations are emitted via tlLogger so
    // production runs produce baselines for backend perf work. See MC20-18232.
    const pullStartedAt = performance.now();
    const measureTier = async <T>(
      tierName: string,
      fn: () => Promise<T>,
    ): Promise<T> => {
      const startedAt = performance.now();
      try {
        const result = await fn();
        tlLogger.log(method, {
          message: `⏱ [perf] tier complete`,
          tier: tierName,
          durationMs: Math.round(performance.now() - startedAt),
          status: 'ok',
        });
        return result;
      } catch (error) {
        tlLogger.log(method, {
          message: `⏱ [perf] tier threw`,
          tier: tierName,
          durationMs: Math.round(performance.now() - startedAt),
          status: 'error',
        });
        throw error;
      }
    };

    // Helper: run a named tier of lazy promises in parallel (allSettled so one failure doesn't halt others)
    const runTier = async (
      calls: { name: string; fn: () => Promise<unknown> }[],
      tierLabel: string,
    ): Promise<void> => {
      // Wrap each API call with timing while preserving the original semantics.
      const timedCalls = calls.map((api) => {
        const startedAt = performance.now();
        return api
          .fn()
          .then((value) => {
            tlLogger.log(method, {
              message: `⏱ [perf] api complete`,
              tier: tierLabel,
              api: api.name,
              durationMs: Math.round(performance.now() - startedAt),
              status: 'ok',
            });
            return value;
          })
          .catch((reason) => {
            tlLogger.log(method, {
              message: `⏱ [perf] api failed`,
              tier: tierLabel,
              api: api.name,
              durationMs: Math.round(performance.now() - startedAt),
              status: 'error',
            });
            throw reason;
          });
      });
      const results = await Promise.allSettled(timedCalls);
      results.forEach((result, index) => {
        const apiName = calls[index].name;
        if (result.status === 'fulfilled') {
          tlLogger.log(method, {
            message: `✅ [${tierLabel}] ${apiName} API succeeded`,
          });
        } else {
          tlLogger.error(method, {
            message: `❌ [${tierLabel}] ${apiName} API failed`,
            error: result.reason,
          });
        }
      });
    };

    // TIER 1: User information (must complete before coaching/courses so profile IDs are available)
    syncStateManager.updateProgress(20, 'Loading data...');

    const tier1Calls: { name: string; fn: () => Promise<unknown> }[] = [
      {
        name: 'rolesAndPermission',
        fn: () => fetchRolesAndPermission(lastSyncedAt),
      },
      { name: 'profiles', fn: () => fetchProfiles(lastSyncedAt) },
      {
        name: 'allCoachProfiles',
        fn: () => fetchAllCoachProfiles(lastSyncedAt),
      },
      // Promoted out of Tier 3: timetable data drives the first screen after
      // login and has no profile-id dependency (only last_pulled_at). Running
      // it in parallel with user info lets the Timetable page unblock in
      // ~1-3s instead of waiting behind Tier 1 + Tier 2 (~60s+).
      { name: 'bookChapters', fn: () => fetchBookChaptersV2(lastSyncedAt) },
    ];

    if (!notSedBalochistanRegion) {
      tier1Calls.push({
        name: 'schoolGroups',
        fn: () => fetchSchoolsGroups(lastSyncedAt),
      });
    }

    await measureTier('tier1-user-information', () =>
      runTier(tier1Calls, 'Tier1: user-information'),
    );

    // TIER 2: Coaching (profile-dependent; 3-phase observation loader preserved)
    // Errors are caught and logged — a coaching failure must not block Tier 3
    // (teacher training courses) for teacher-only profiles.
    syncStateManager.updateProgress(30, 'Loading your schedule...');
    await measureTier('tier2a-visits', () => fetchVisits(lastSyncedAt)).catch(
      (error) => tlLogger.error(method, { message: 'tier2a-visits failed', error }),
    );

    syncStateManager.updateProgress(45, 'Loading observations...');

    await measureTier('tier2b-observations', async () => {
      try {
        const { totalPages, nextStartPage } = await measureTier(
          'tier2b-obs-page1',
          () => fetchInitialObservationTemplates(lastSyncedAt, 1),
        );
        tlLogger.log(method, {
          message: 'Fetched initial observation templates',
          totalPages,
          nextStartPage,
        });

        syncStateManager.updateProgress(60, 'Loading teachers & schools...');
        await measureTier('tier2b-obs-context', () =>
          fetchObservationContext(lastSyncedAt),
        );
        tlLogger.log(method, {
          message: 'Fetched observation context - UI can now render cards!',
        });

        if (nextStartPage <= totalPages) {
          tlLogger.log(method, {
            message: `Starting fetch of remaining pages from ${nextStartPage} to ${totalPages}...`,
            tier: 'tier2b-obs-remaining-pages',
            pageStart: nextStartPage,
            pageEnd: totalPages,
            pageCount: totalPages - nextStartPage + 1,
          });

          const remainingPages = totalPages - nextStartPage + 1;
          await measureTier('tier2b-obs-remaining-pages', () =>
            fetchObservationTemplatesRemainingPages(
              lastSyncedAt,
              nextStartPage,
              totalPages,
              (currentPage, _totalPages) => {
                const pagesFetched = currentPage - nextStartPage + 1;
                const progressPercent =
                  60 + (pagesFetched / remainingPages) * 35;
                syncStateManager.updateProgress(
                  Math.round(progressPercent),
                  'Loading observation history...',
                );
              },
            ),
          );
          tlLogger.log(method, {
            message: `Completed fetching remaining pages`,
          });
        }

        await measureTier('tier2b-obs-feedbacks', () =>
          fetchObservationFeedbacks(lastSyncedAt),
        );
        await measureTier('tier2b-dc-chat-sessions', () =>
          pullChatSessions(lastSyncedAt),
        );
      } catch (error) {
        tlLogger.error(method, {
          message: 'Error fetching observation data — continuing to Tier 3',
          error,
        });
        // Do NOT rethrow — a coaching sync failure must not block teacher
        // training data (Tier 3). Teacher-only profiles have no observations
        // and the API returns an error for them.
      }
    });

    // TIER 3: Courses & assessment (depends on profile IDs from TIER 1).
    // bookChapters moved to Tier 1 — it has no profile-id dependency and
    // drives the first post-login screen.
    await runTier(
      [
        { name: 'courses', fn: () => fetchCourseData(lastSyncedAt) },
        {
          name: 'reportCards',
          fn: () =>
            fetchStudentAssessmentDataV1(
              studentDB.getCheckPoints.length === 0 ? null : lastSyncedAt,
            ),
        },
      ],
      'Tier3: courses-and-assessment',
    );

    // TIER 4: Feature-gated and independent APIs (fire-and-forget style via allSettled, no dependency on earlier data)
    const tier4Calls: { name: string; fn: () => Promise<unknown> }[] = [
      { name: 'notifications', fn: () => fetchNotifications(lastSyncedAt) },
    ];

    if (isExamGeneratorEnabled) {
      tier4Calls.push({
        name: 'egSupportedConfig',
        fn: () => fetchExamGeneratorSupportedConfig(),
      });
    }

    await measureTier('tier4-feature-gated', () =>
      runTier(tier4Calls, 'Tier4: feature-gated'),
    );

    // TIER 5: Fire-and-forget async tasks (intentionally not awaited to avoid blocking)
    userDB.getUserActiveProfile().then(async (activeProfile) => {
      if (activeProfile) {
        const flnAssessments = await fetchFlnAssessments(activeProfile.id);
        tlLogger.log(method, { flnAssessments });
      }
    });

    syncStateManager.updateProgress(95, 'Finishing up...');
    syncStateManager.updateProgress(100, 'Loading complete!');

    // Final perf marker: total foreground pull wall time. Compare against
    // per-tier durations above to see where the time budget actually goes.
    tlLogger.log(method, {
      message: '⏱ [perf] pullChanges total wall time',
      tier: 'pullChanges-total',
      durationMs: Math.round(performance.now() - pullStartedAt),
      status: 'ok',
    });

    tlLogger.log(method, {
      message:
        'Sync completed (tiered: user-info → coaching → courses → feature-gated)',
    });
  }

  handleNetworkError = async (method: string, error: AxiosError) => {
    tlLogger.error(method, { message: error.message });
    if (!this.pushRetry) {
      // we only want to retry once if there is error
      this.pushRetry = true;
      tlLogger.log(method, { message: 'Retrying to push changing...' });
      await this.pushChanges();
    }
  };
  handleFailedRecords = async (method: string, failedRecords: any) => {
    if (Object.keys(failedRecords).length) {
      tlLogger.verbose(method, {
        message: 'Records with invalid data.',
        failedRecords,
      });
      //Mark failed records as failed so it can't be retried
      const updates = await this.postSyncChanges(
        failedRecords,
        SyncRecordStatus.Failed,
      );
      analyticsService.trackEvent(EventNames.recordsFailedToSync, {
        records: JSON.stringify(failedRecords),
      });
      tlLogger.verbose(method, {
        message: 'Marked records failed.',
        updates,
      });
    }
  };
  handleRetryRecords = async (method: string, retryRecords: any) => {
    if (Object.keys(retryRecords).length) {
      tlLogger.verbose(method, {
        message: 'Records with valid data.',
        retryRecords,
      });
      if (!this.pushRetry) {
        tlLogger.log(method, { message: 'Retrying to push changing...' });
        // we only want to retry once if there is validation error
        this.pushRetry = true;
        await this.pushChanges();
        return;
      }
    }
  };

  handleValidationError = async (
    method: string,
    error: AxiosError,
    dataChanges: any,
  ) => {
    const failedRecords: Record<string, any> = {};
    const retryRecords: SyncChanges = {};
    const data: Record<string, any> = error.response?.data || {};
    if (!Object.keys(data).length) return;
    for (const key in data) {
      //TODO: currently just handling the failed changes in created records update to handle other types of changes
      const actualChanges = dataChanges[key].created;
      const invalidRecords: Record<string, any> = [];
      const validRecords: Record<string, any> = [];
      data[key].forEach((record: Record<string, any>, index: number) => {
        if (Object.keys(record).length) {
          invalidRecords.push(actualChanges[index]);
        } else {
          validRecords.push(actualChanges[index]);
        }
      });
      failedRecords[key] = { created: invalidRecords };
      retryRecords[key] = { created: validRecords };
    }
    await this.handleFailedRecords(method, failedRecords);
    await this.handleRetryRecords(method, retryRecords);
  };

  filterCreatedRecords = async (): Promise<SyncChanges> => {
    const syncStartedAt = await syncedDBModel.getSyncStartedAt();
    const updatedDataChanges = await this.getChangesToSync();

    if (syncStartedAt === null) {
      return updatedDataChanges;
    }

    const filteredDataChanges: SyncChanges = {};

    for (const tableName in updatedDataChanges) {
      const tableChanges = updatedDataChanges[tableName];
      const possibleUpdatedRecords = tableChanges.created || [];

      // only get the records that were NOT updated during the push call
      const filteredUpdatedRecords = possibleUpdatedRecords.filter(
        (record: { lastLocalModifiedAt: number }) => {
          return record.lastLocalModifiedAt <= syncStartedAt;
        },
      );

      filteredDataChanges[tableName] = {
        ...tableChanges,
        created: filteredUpdatedRecords,
      };
    }

    return filteredDataChanges;
  };

  /**
   * The function performs the following steps:
   * 1. Logs the start of the push operation.
   * 2. Retrieves the changes to be synced.
   * 3. If there are no changes to be synced, logs this and returns.
   * 4. Attempts to post the changes to the remote server.
   * 5. If the post is successful, it logs the server response, marks the records as synced, and logs this.
   * 6. If the post fails with a server error, network error, or timeout, it logs the error message. If this is the first retry, it sets a flag to prevent further retries and attempts to push the changes again.
   * 7. If the post fails with a 400 status code (Bad Request), it separates the changes into valid and invalid records. Invalid records are marked as failed and this is logged. Valid records are retried if this is the first retry.
   * 8. If the post fails for any other reason, it logs the error message.
   */
  async pushChanges(method = 'SyncManager/performSync'): Promise<boolean> {
    tlLogger.log(method, { message: 'Pushing changes.' });
    const dataChanges = await this.getChangesToSync();
    tlLogger.log(method, { message: 'Got changes to sync.' });
    const keys = Object.keys(dataChanges);
    if (!keys.length) {
      tlLogger.log(method, { message: 'No changes to sync.' });
      return true;
    }
    const user = userCache.getUserFromCache();

    try {
      if (user) {
        await this.updateSyncStartedAt();
        const response = await apiService.post<IResponseData>(
          `api/v1/push-sync/?c=${Date.now()}`,
          dataChanges,
        );

        // if there were any changes in same records DURING the push call then we need to remove them
        // because https://docs.google.com/document/d/1SEkhCsh8mbhRmsOlijrJF8cyi_mVEf-_qCPIvDrUcFc/edit?tab=t.0#heading=h.fg6w83mtuxa3
        const filteredDataChanges = await this.filterCreatedRecords();
        // on pull call backend send the updated objects back, and our frontend logic is to update the local objects
        // we do not want to update the local objects if they were updated during the push call
        if (!isEqual(dataChanges, filteredDataChanges)) {
          tlLogger.log(method, {
            message: 'Changes were made during push call. Skip pulling',
          });
          // every object that was made during the push call will be marked as synced, EXCEPT the ones that were created/updated during the push call
          // they will be synced on the next push call, filteredDataChanges will not have them
          await this.postSyncChanges(
            filteredDataChanges,
            SyncRecordStatus.Synced,
          );
          return false;
        }
        this.pushRetry = false;
        tlLogger.verbose(method, {
          message: 'Response from server.',
          response,
        });

        //////// adding duplicate students into database //////
        await saveDuplicateStudents(response.data);

        const updates = await this.postSyncChanges(
          filteredDataChanges,
          SyncRecordStatus.Synced,
        );
        tlLogger.verbose(method, {
          message: 'Marked records synced.',
          updates,
        });
        // will udpate this method once the backend will provide the structure error object for failed records
        const failedRecord = await this.markFailedRecords(
          response.data.failedTablesData,
          dataChanges,
          response.data,
        );

        tlLogger.verbose(method, {
          message: 'Marked records Failed.',
          failedRecord,
        });
      }
    } catch (e) {
      const error = e as AxiosError;
      if (
        error.response?.status === 500 ||
        error.message === 'Network Error' ||
        error.code === 'ECONNABORTED'
      ) {
        await this.handleNetworkError(method, error);
        return true;
      }
      if (error.response?.status === 400) {
        await this.handleValidationError(method, error, dataChanges);
        return true;
      }
      tlLogger.error(method, { message: 'Error pushing changes.', e });
      return true;
    }
    return true;
  }

  async performSync(syncWorker: Worker | null) {
    const method = 'SyncManager/performSync';
    tlLogger.start(method);
    if (syncWorker) {
      syncWorker.postMessage({ action: 'pull' });
      tlLogger.log(method);
    } else {
      tlLogger.log(method, {
        message: 'Worker not available, going on main thread.',
      });
      const lastSyncedAt = await this.getLastSyncedAt();
      // we don't want to push anything first time
      // until the first sync is completed.
      if (lastSyncedAt) {
        this.pushRetry = false;
        const shouldProceed = await this.pushChanges(method);
        if (shouldProceed) {
          await this.pullChanges(method);
          await this.updateSyncedAt();
        }
      } else {
        await this.pullChanges(method);
        await this.updateSyncedAt();
      }
      tlLogger.log(method, {
        message: 'Changes completed ',
        timestamp: new Date(),
      });
    }
    tlLogger.end(method);
  }
}

const saveDuplicateStudents = async (data: IResponseData) => {
  const studentDuplicate = data.failedTablesData?.studentProfile;
  if (studentDuplicate) {
    await studentDB.addBulkDuplicateStudents(studentDuplicate.created ?? []);
    await studentDB.addBulkDuplicateStudents(studentDuplicate.updated ?? []);
  }
};

export const syncManager = new SyncManager(
  // only use [0] for pull sync and [1] for push sync [2] for failed pull sync
  [
    {
      name: teacherTrainingDB.name,
      model: teacherTrainingDB,
    },
    {
      name: lessonPlanDB.name,
      model: lessonPlanDB,
    },
    {
      name: examGeneratorDB.name,
      model: examGeneratorDB,
    },
    {
      name: flnAssessmentDB.name,
      model: flnAssessmentDB,
    },
    {
      name: studentDB.name,
      model: studentDB,
    },
    {
      name: notificationDb.name,
      model: notificationDb,
    },
    {
      name: coachingDB.name,
      model: coachingDB,
    },
    {
      name: userDB.name,
      model: userDB,
    },
    {
      // Pull-only: DC chat sessions hydrate from the server via the explicit
      // pullChatSessions() call in pullChanges. NOT a write table — sessions are
      // created exclusively through the live chat/start endpoint, never via the
      // generic push-sync endpoint (which has no DC awareness).
      name: digitalCoachChatDB.name,
      model: digitalCoachChatDB,
    },
  ],
  [
    {
      name: analyticsDB.name,
      model: analyticsDB,
    },
    {
      name: teacherTrainingDB.name,
      model: teacherTrainingDB,
    },
    {
      name: examGeneratorDB.name,
      model: examGeneratorDB,
    },
    {
      name: lessonPlanDB.name,
      model: lessonPlanDB,
    },
    {
      name: studentDB.name,
      model: studentDB,
    },
    {
      name: notificationDb.name,
      model: notificationDb,
    },
    {
      name: coachingDB.name,
      model: coachingDB,
    },
    {
      name: userDB.name,
      model: userDB,
    },
  ],
  [
    { name: userDB.name, model: userDB },
    { name: coachingDB.name, model: coachingDB },
    { name: lessonPlanDB.name, model: lessonPlanDB },
    { name: teacherTrainingDB.name, model: teacherTrainingDB },
  ],
);
